import { useEffect, useRef } from 'react';

const COLORS = ['#5B4FE8', '#10B981', '#F59E0B', '#EC4899', '#4338CA'];

/**
 * One-shot celebratory confetti (C2). Fires ONCE on mount (empty-dep effect),
 * never on re-render. Respects prefers-reduced-motion: when the user prefers
 * reduced motion the effect no-ops — no canvas work, no animation, the screen
 * underneath shows statically. Self-contained canvas (no confetti library) so it
 * adds ~1KB of code, not a dependency — the bundle stays flat. The canvas is
 * fixed + pointer-events-none + aria-hidden, so it never blocks taps or a11y.
 */
export default function ConfettiBurst({ count = 90, durationMs = 1400 }: { count?: number; durationMs?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Reduced-motion: do nothing (no animation).
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx || typeof requestAnimationFrame !== 'function') return;

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.clientWidth || window.innerWidth;
    const H = canvas.clientHeight || window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    interface P { x: number; y: number; vx: number; vy: number; rot: number; vr: number; size: number; color: string }
    const parts: P[] = Array.from({ length: count }, () => ({
      x: W / 2 + (Math.random() - 0.5) * W * 0.3,
      y: H * 0.35,
      vx: (Math.random() - 0.5) * 8,
      vy: Math.random() * -9 - 4,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      size: 5 + Math.random() * 6,
      color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
    }));

    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = now - start;
      ctx.clearRect(0, 0, W, H);
      for (const p of parts) {
        p.vy += 0.25; // gravity
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, 1 - t / durationMs);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }
      if (t < durationMs) raf = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, W, H);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ONCE on mount — not on re-render

  return <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none fixed inset-0 z-50 w-full h-full" />;
}
